import React, { useMemo, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { MaybeTooltip } from '@modernloop/shared/components';
import { CheckIcon, LinkIcon, ModulesIcon } from '@modernloop/shared/icons';
import { Avatar, Box, Stack, Typography } from '@mui/material';

import { AutoCompleteOptionType } from './types';

interface Props {
  selected: boolean;
  hasInterviewers: boolean;
  name: string;
  type: AutoCompleteOptionType;
  photoUrl?: string | undefined;
  email?: string | undefined;
}

export const OptionComponent: React.FC<Props> = ({ selected, hasInterviewers, name, type, photoUrl, email }) => {
  const { t } = useTranslation();
  const [optionHovered, setOptionHovered] = useState(false);

  const label = useMemo(() => {
    if (optionHovered) {
      let labelText = '';
      if (type === AutoCompleteOptionType.INTERVIEW || type === AutoCompleteOptionType.SEAT) {
        if (selected) {
          labelText = t('hiring-dashboard-interviewers:interviewers.plan.seats.remove');
        } else if (hasInterviewers) {
          labelText = t('hiring-dashboard-interviewers:interviewers.plan.seats.replaceAll');
        } else {
          labelText = t('hiring-dashboard-interviewers:interviewers.plan.seats.add');
        }
      } else {
        labelText = selected
          ? t('hiring-dashboard-interviewers:interviewers.plan.seats.remove')
          : t('hiring-dashboard-interviewers:interviewers.plan.seats.add');
      }
      return labelText;
    }

    return selected ? <CheckIcon /> : undefined;
  }, [selected, hasInterviewers, optionHovered, type, t]);

  let icon: JSX.Element | null = null;
  if (type === AutoCompleteOptionType.INTERVIEW) {
    icon = <ModulesIcon />;
  } else if (type === AutoCompleteOptionType.SEAT) {
    icon = <LinkIcon />;
  } else {
    icon = <Avatar sx={{ width: '32px', height: '36px' }} alt={name} src={photoUrl} />;
  }

  return (
    <Box
      width="100%"
      onMouseOver={() => {
        setOptionHovered(true);
      }}
      onMouseOut={() => {
        setOptionHovered(false);
      }}
      onFocus={() => {
        setOptionHovered(true);
      }}
      onBlur={() => {
        setOptionHovered(false);
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" width="100%" display="flex">
        <Stack direction="row" spacing={1} alignItems="center" flex={1} display="flex" width="100%">
          {icon}
          <Stack direction="column" display="flex" overflow="hidden" width="90%">
            <MaybeTooltip label={name} title={name} />
            {email ? (
              <Typography variant="caption" color="text.secondary">
                {email}
              </Typography>
            ) : null}
          </Stack>
          <Typography flexShrink={0} variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};
